import React from 'react';
import { FaLock } from 'react-icons/fa';

/**
 * Locked-teaser primitives for /start department pages.
 *
 * These mirror the real portal shell (header · tab nav · overview cards) but
 * render sample content only. Start visitors are prospects: they have no
 * companyId and no owner account, so the live portal cards
 * (DepartmentOverviewTopRow, DepartmentWhatsNewCard, DepartmentImplementationCard)
 * cannot be reused here — they read company-scoped Firestore data. The layout is
 * replicated; the data is not.
 *
 * Tab navs are driven by the real `src/utils/*Tabs.js` configs so the locked
 * previews cannot drift out of sync with the portal's actual sections.
 */

/** Dimmed, non-interactive wrapper with a lock badge — the teaser treatment. */
export const StartLockedCard = ({ className = '', children }) => (
  <div className={`relative ${className}`}>
    <div className="absolute top-3 right-3 z-10">
      <FaLock className="text-gray-400 dark:text-gray-500 text-sm" aria-hidden="true" />
    </div>
    <div className="pointer-events-none select-none opacity-60" aria-hidden="true">
      {children}
    </div>
  </div>
);

const cardBase =
  'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm';

/** Skeleton row used inside the locked cards in place of real feed data. */
const SkeletonRow = ({ widthClass = 'w-2/3' }) => (
  <div className="flex items-center gap-3 p-2.5">
    <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-700 shrink-0" />
    <div className="flex-1 space-y-2">
      <div className={`h-3 rounded bg-gray-100 dark:bg-gray-700 ${widthClass}`} />
      <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-gray-700" />
    </div>
  </div>
);

/** Locked replica of DepartmentCommunityWidget. */
const LockedCommunityCard = ({ className = '' }) => (
  <StartLockedCard className={className}>
    <div className={`${cardBase} p-5 h-full flex flex-col`}>
      <div className="flex items-center justify-center gap-2 mb-4">
        <span className="text-base leading-none" aria-hidden="true">💬</span>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Community</h2>
      </div>
      <div className="space-y-1 flex-1">
        <SkeletonRow widthClass="w-3/4" />
        <SkeletonRow widthClass="w-1/2" />
        <SkeletonRow widthClass="w-2/3" />
      </div>
    </div>
  </StartLockedCard>
);

/** Locked replica of DepartmentRoadmap. */
const LockedRoadmapCard = ({ className = '' }) => (
  <StartLockedCard className={className}>
    <div className={`${cardBase} p-4 h-full flex flex-col`}>
      <div className="flex items-center justify-center gap-2 mb-4">
        <span className="text-base leading-none" aria-hidden="true">🗺️</span>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Roadmap</h2>
      </div>
      <div className="flex-1 flex flex-col justify-center">
        <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden mb-3">
          <div className="h-full w-1/3 bg-red-200 dark:bg-red-900/50 rounded-full" />
        </div>
        <div className="h-3 w-1/2 rounded bg-gray-100 dark:bg-gray-700 mb-2" />
        <div className="h-2.5 w-3/4 rounded bg-gray-100 dark:bg-gray-700" />
      </div>
    </div>
  </StartLockedCard>
);

/** Locked replica of DepartmentGoals. */
const LockedGoalsCard = ({ className = '' }) => (
  <StartLockedCard className={className}>
    <div className={`${cardBase} p-4 h-full flex flex-col`}>
      <div className="flex items-center justify-center gap-2 mb-4">
        <span className="text-base leading-none" aria-hidden="true">🎯</span>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Goals</h2>
      </div>
      <div className="space-y-4 flex-1">
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <div className="h-2.5 w-2/3 rounded bg-gray-100 dark:bg-gray-700 mb-2" />
            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full bg-red-200 dark:bg-red-900/50 rounded-full"
                style={{ width: `${[70, 45, 25][i]}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </StartLockedCard>
);

/**
 * Locked replica of DepartmentOverviewTopRow — the shared top row on every
 * portal department page: [ half Community ][ quarter Roadmap ][ quarter Goals ].
 */
export const StartLockedOverviewRow = ({ showGoals = true }) => (
  <div
    className={`grid grid-cols-1 ${showGoals ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-6`}
  >
    <LockedCommunityCard className="lg:col-span-2" />
    <LockedRoadmapCard />
    {showGoals && <LockedGoalsCard />}
  </div>
);

/** Locked replica of DepartmentWhatsNewCard. */
export const StartLockedWhatsNewCard = () => (
  <StartLockedCard>
    <div className={`${cardBase} h-full flex flex-col`}>
      <div className="flex items-center justify-center gap-2 p-5 pb-4">
        <span className="text-base leading-none" aria-hidden="true">✨</span>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">What's New</h2>
      </div>
      <div className="flex-1 px-3 pb-3 space-y-0.5">
        <SkeletonRow widthClass="w-3/4" />
        <SkeletonRow widthClass="w-1/2" />
        <SkeletonRow widthClass="w-2/3" />
        <SkeletonRow widthClass="w-3/5" />
      </div>
    </div>
  </StartLockedCard>
);

/**
 * Locked replica of DepartmentImplementationCard (strategy calls + Schedule Call).
 */
export const StartLockedImplementationCard = () => (
  <StartLockedCard>
    <div className={`${cardBase} h-full flex flex-col`}>
      <div className="flex items-center justify-center gap-2 p-5 pb-4">
        <span className="text-base leading-none" aria-hidden="true">⏱️</span>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          Implementation Hours
        </h2>
      </div>
      <div className="px-5 pb-5 flex-1 flex flex-col">
        <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden mb-4">
          <div className="h-full w-2/5 bg-red-200 dark:bg-red-900/50 rounded-full" />
        </div>
        <div className="space-y-1 flex-1">
          <SkeletonRow widthClass="w-2/3" />
          <SkeletonRow widthClass="w-1/2" />
          <SkeletonRow widthClass="w-3/5" />
        </div>
        <div className="h-9 rounded-lg bg-red-200 dark:bg-red-900/50 mt-4" />
      </div>
    </div>
  </StartLockedCard>
);

/** Locked replica of HRHiringStatusCard (HR's right-hand card). */
export const StartLockedHiringStatusCard = () => (
  <StartLockedCard>
    <div className={`${cardBase} h-full flex flex-col`}>
      <div className="flex items-center justify-center gap-2 p-5 pb-4">
        <span className="text-base leading-none" aria-hidden="true">🤝</span>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Hiring Status</h2>
      </div>
      <div className="px-5 pb-5 flex-1 space-y-3">
        <SkeletonRow widthClass="w-3/5" />
        <SkeletonRow widthClass="w-2/3" />
        <SkeletonRow widthClass="w-1/2" />
      </div>
    </div>
  </StartLockedCard>
);
